import { defineStore } from "pinia";

const BASE_URL = import.meta.env.VITE_APP_API_URL;

function gerarIdRandomico() {
    const timeStamp = new Date().getTime().toString(36);
    const valorAleatorio = Math.random().toString(36).substr(2, 9);
    return timeStamp + '-' + valorAleatorio;
}

export const useUserStore = defineStore({
    id: "user",

    persist: {
        enabled: true,
        strategies: [
            {
                key: 'userStore',
                storage: localStorage,
                // Persiste apenas os campos essenciais
                paths: ['user', 'token', 'objetoUsuario', 'lastTokenCheck', 'isInitialized', 'permissoes', 'grupos', 'isAdmin']
            }
        ]
    },

    state: () => ({
        user: null,
        token: "",
        numero_pedido: null,
        objetoUsuario: null,
        GrupoDeProdutosPinia: [],
        GrupoDeProdutosSelecionadoPinia: null,
        ProdutoSelecionadoPinia: null,
        ClienteSelecionadoPinia: null,
        TabelaSelecionadaPinia: null,
        ItensPedidoPinia: [],
        // Controle de estado de verificação
        isVerifyingToken: false,
        lastTokenCheck: null,
        isInitialized: false, // ✨ NOVO: controla se já verificou na inicialização
        // Sistema de Permissões
        permissoes: [], // Array de permissões do usuário
        grupos: [], // Grupos que o usuário pertence
        isAdmin: false, // Se o usuário é administrador
    }),

    getters: {
        isAuthenticated: (state) => {
            return state.user !== null && state.token !== "";
        },

        getUsuarioLogado: (state) => {
            return state.objetoUsuario;
        },

        // Verifica se usuário tem permissão para uma aplicação
        hasPermission: (state) => (appName, permission = 'access') => {
            // IMPORTANTE: Admin também precisa ter permissão configurada no grupo
            const app = state.permissoes.find(p => p.app_name === appName);
            if (!app) return false;
            
            return app[permission] === true;
        },

        // Verifica se usuário tem acesso a uma aplicação
        hasAccess: (state) => (appName) => {
            // IMPORTANTE: Admin também precisa ter permissão configurada no grupo
            const app = state.permissoes.find(p => p.app_name === appName);
            return app && app.access === true;
        },

        // Obtém todas as permissões de uma aplicação
        getAppPermissions: (state) => (appName) => {
            const app = state.permissoes.find(p => p.app_name === appName);
            return app || null;
        },

        // Verificar se precisa validar token
        needsTokenValidation: (state) => {
            if (!state.token || !state.lastTokenCheck) return true;
            
            // Verifica a cada 5 minutos
            const fiveMinutes = 5 * 60 * 1000;
            return (Date.now() - state.lastTokenCheck) > fiveMinutes;
        }
    },

    actions: {
        // ✨ CORRIGIDO: Usar a rota correta do Sanctum
        async verificarTokenValido() {
            try {
                if (!this.token) {
                    // console.log('❌ Nenhum token encontrado');
                    return false;
                }

                // Evita múltiplas verificações simultâneas
                if (this.isVerifyingToken) {
                    // console.log('⏳ Verificação de token já em andamento...');
                    return true;
                }

                this.isVerifyingToken = true;
                // console.log('🔍 Verificando validade do token...');
                
                // ✨ MUDANÇA: Usar a rota correta do Sanctum
                const url = `${BASE_URL}/verificar-token-valido`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${this.token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    
                    // ✨ MUDANÇA: Verificar a estrutura correta da resposta
                    if (data.valid === true) {
                        this.lastTokenCheck = Date.now();
                        // console.log('✅ Token válido');
                        
                        // Atualizar dados do usuário se necessário
                        if (data.user && !this.objetoUsuario) {
                            this.objetoUsuario = data.user;
                        }
                        
                        return true;
                    } else {
                        //     console.log('❌ Token inválido - resposta do servidor');
                        await this.logout();
                        return false;
                    }
                } else if (response.status === 401 || response.status === 403) {
                   // console.log('❌ Token expirado ou inválido - status:', response.status);
                    await this.logout();
                    return false;
                } else {
                   // console.log('⚠️ Erro na verificação do token - status:', response.status);
                    return false;
                }
                
            } catch (error) {
                console.error('💥 Erro ao validar token:', error);
                return false;
            } finally {
                this.isVerifyingToken = false;
            }
        },

        // ✨ NOVO: Método específico para inicialização da aplicação
        async initializeAuth() {
            try {
                if (this.isInitialized) {
                    return this.isAuthenticated;
                }

               // console.log('🚀 Inicializando autenticação...');
                
                // Se não tem token, marca como inicializado e retorna false
                if (!this.token) {
                   // console.log('❌ Nenhum token encontrado - usuário não logado');
                    this.isInitialized = true;
                    return false;
                }

                // Verifica se o token ainda é válido
                const tokenValido = await this.verificarTokenValido();
                
                if (!tokenValido) {
                   // console.log('❌ Token inválido - limpando sessão');
                    await this.logout();
                    this.isInitialized = true;
                    return false;
                }

                const login = this.objetoUsuario?.login || this.user?.username;
                if (login) {
                    await this.carregarPermissoes(login);
                }

               // console.log('✅ Autenticação inicializada com sucesso');
                this.isInitialized = true;
                return true;
                
            } catch (error) {
                console.error('💥 Erro ao inicializar autenticação:', error);
                await this.logout();
                this.isInitialized = true;
                return false;
            }
        },

        // Método para inicializar/restaurar sessão (mantém compatibilidade)
        async inicializarSessao() {
            return await this.initializeAuth();
        },

        // ✨ MELHORADO: Logout com chamada para o backend
        async logout() {
            try {
               // console.log('🚪 Fazendo logout...');
                
                // Tentar fazer logout no servidor se tiver token
                if (this.token) {
                    try {
                        await fetch(`${BASE_URL}/logout`, {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${this.token}`,
                                'Content-Type': 'application/json',
                                'Accept': 'application/json'
                            }
                        });
                       // console.log('✅ Logout no servidor realizado');
                    } catch (error) {
                        //console.log('⚠️ Erro no logout do servidor (continuando logout local):', error);
                    }
                }
                
                // Limpa todos os dados
                this.resetState();
                
                // Limpa localStorage manualmente (garantia)
                localStorage.removeItem('userStore');
                
                //console.log('✅ Logout realizado com sucesso');
                
            } catch (error) {
                console.error('💥 Erro durante logout:', error);
                // Mesmo com erro, limpa os dados locais
                this.resetState();
                localStorage.removeItem('userStore');
            }
        },

        // Método de login (mantém o mesmo)
        // ✅ MÉTODO LOGIN CORRIGIDO
async login(login, senha) {
    try {
       // console.log('🔐 Tentando fazer login...');
        
        const url = `${BASE_URL}/validar-usuario`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },
            body: JSON.stringify({ login, senha }),
        });

        //console.log('📡 Status da resposta:', response.status);
        //console.log('📡 Response OK:', response.ok);

        // ✅ CORREÇÃO: Sempre tentar ler o JSON da resposta
        let data;
        try {
            data = await response.json();
           // console.log('📦 Dados da resposta:', data);
        } catch (jsonError) {
            console.error('❌ Erro ao fazer parse do JSON:', jsonError);
            return { 
                success: false, 
                message: 'Erro na resposta do servidor',
                error_code: 'SYS_001',
                technical_message: 'Resposta inválida do servidor'
            };
        }

        // ✅ VERIFICAR SE É SUCESSO (status 200 E success: true)
        if (response.ok && data.success) {
            // Login bem-sucedido
            this.objetoUsuario = data.user;
            this.user = { username: login };
            this.token = data.token;
            this.lastTokenCheck = Date.now();
            this.isInitialized = true;
            
            // Carregar permissões do usuário
            await this.carregarPermissoes(login);
            
          //  console.log('✅ Login realizado com sucesso');
            return { 
                success: true, 
                message: 'Login realizado com sucesso',
                user: data.user,
                token: data.token
            };
        } 
        // ✅ VERIFICAR SE É ERRO DE AUTENTICAÇÃO (401, 403, 400) COM DADOS ESTRUTURADOS
        else if ((response.status === 401 || response.status === 403 || response.status === 400) && data) {
            console.log('❌ Erro de autenticação com dados estruturados');
            this.resetState();
            
            // ✅ RETORNAR OS DADOS EXATOS DA API (incluindo error_code)
            return {
                success: false,
                message: data.message || 'Credenciais inválidas',
                error_code: data.error_code || 'USER_001',
                technical_message: data.technical_message || data.message,
                details: data.details || null
            };
        }
        // ✅ VERIFICAR SE É RESPOSTA 200 MAS COM success: false
        else if (response.ok && !data.success) {
            //console.log('❌ Resposta OK mas login falhou');
            this.resetState();
            
            return {
                success: false,
                message: data.message || 'Credenciais inválidas',
                error_code: data.error_code || 'USER_001',
                technical_message: data.technical_message || data.message,
                details: data.details || null
            };
        }
        // ✅ OUTROS ERROS HTTP
        else {
            console.log('❌ Erro HTTP não tratado:', response.status);
            this.resetState();
            
            return { 
                success: false, 
                message: data?.message || 'Erro no servidor',
                error_code: data?.error_code || 'SYS_001',
                technical_message: `HTTP ${response.status}: ${data?.message || 'Erro do servidor'}`
            };
        }
        
    } catch (error) {
        console.error('💥 Erro ao fazer login (catch):', error);
        this.resetState();
        
        // ✅ VERIFICAR SE É ERRO DE REDE
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            return { 
                success: false, 
                message: 'Erro de conexão com o servidor',
                error_code: 'SYS_002',
                technical_message: 'Erro de rede: ' + error.message
            };
        }
        
        // ✅ OUTROS ERROS
        return { 
            success: false, 
            message: 'Erro interno do sistema',
            error_code: 'SYS_001',
            technical_message: error.message || 'Erro desconhecido'
        };
    }
},


        // ✨ MELHORADO: resetState inclui isInitialized
        resetState() {
            this.user = null;
            this.token = "";
            this.objetoUsuario = null;
            this.GrupoDeProdutosPinia = [];
            this.GrupoDeProdutosSelecionadoPinia = null;
            this.ProdutoSelecionadoPinia = null;
            this.ClienteSelecionadoPinia = null;
            this.TabelaSelecionadaPinia = null;
            this.ItensPedidoPinia = [];
            this.isVerifyingToken = false;
            this.lastTokenCheck = null;
            this.isInitialized = false; // ✨ NOVO
            this.permissoes = [];
            this.grupos = [];
            this.isAdmin = false;
        },

        // Carrega permissões do usuário do backend
        async carregarPermissoes(login) {
            try {
                if (!this.token) return;

                const url = `${BASE_URL}/permissions/usuarios/${login}/permissoes`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${this.token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    
                    if (data.status === 'sucesso') {
                        this.permissoes = data.permissoes || [];
                        this.grupos = data.grupos || [];
                        this.isAdmin = data.is_admin || false;
                        // console.log('✅ Permissões carregadas:', this.permissoes);
                        return true;
                    }
                }
                
                console.warn('⚠️ Não foi possível carregar permissões');
                return false;
                
            } catch (error) {
                console.error('💥 Erro ao carregar permissões:', error);
                return false;
            }
        },

        // ✨ NOVO: Método para refresh token
        async refreshToken() {
            try {
                if (!this.token) return false;

                const response = await fetch(`${BASE_URL}/refresh-token`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${this.token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    
                    if (data.success) {
                        this.token = data.token;
                        this.lastTokenCheck = Date.now();
                        console.log('✅ Token renovado com sucesso');
                        return true;
                    }
                }
                
                console.log('❌ Erro ao renovar token');
                await this.logout();
                return false;
                
            } catch (error) {
                console.error('💥 Erro ao renovar token:', error);
                await this.logout();
                return false;
            }
        },

        // Seus outros métodos existentes (mantém todos iguais)...
        setnumeropedido(numero) {
            this.numero_pedido = numero;
        },

        atualizarProdutoSelecionado(novosDados) {
            this.ProdutoSelecionadoPinia = {
                ...this.ProdutoSelecionadoPinia,
                ...novosDados,
            };
        },

        adicionarItem(item) {
            const idRandomico = gerarIdRandomico();
            this.ItensPedidoPinia.push({
                ...item,
                id: idRandomico
            });
        },

        removeItemPedidoPinia(idProduto) {
            const indice = this.ItensPedidoPinia.findIndex(item => item.id_produto === idProduto);
            if (indice !== -1) {
                this.ItensPedidoPinia.splice(indice, 1);
            }
        },

        setItensPedidoPiniaAlteracao(itens) {
            this.ItensPedidoPinia = itens;
        },

        setItensPedidoPinia(novoItem) {
            const indice = this.ItensPedidoPinia.findIndex(item => item.id_produto === novoItem.id_produto);
            if (indice !== -1) {
                this.ItensPedidoPinia[indice] = novoItem;
            } else {
                this.ItensPedidoPinia.push(novoItem);
            }
        },

        setClienteSelecionadoPinia(cliente) {
            this.ClienteSelecionadoPinia = cliente;
        },

        setProdutoSelecionadoPinia(produto) {
            this.ProdutoSelecionadoPinia = produto;
        },

        setGrupoDeProdutos(grupoDeProdutos) {
            this.GrupoDeProdutosPinia = grupoDeProdutos;
        },

        getGrupoDeProdutos() {
            return this.GrupoDeProdutosPinia;
        },

        setGrupoDeProdutosSelecionado(GrupoSelecionado) {
            this.GrupoDeProdutosSelecionadoPinia = GrupoSelecionado;
        },

        async setTabelaSelecionada(TabelaSelecionado) {
            this.TabelaSelecionadaPinia = await TabelaSelecionado;
        },

        getIdTabelaDePrecosPinia() {
            return this.TabelaSelecionadaPinia?.id_forma_pagamento;
        },

        initialState() {
            this.user = null;
            this.token = "";
        },
    },
});